import { useEffect, useState } from "react";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { Link, useLocation } from "wouter";

// Schéma pre validáciu resetovania hesla
const resetPasswordSchema = z.object({
  password: z.string()
    .min(8, "Heslo musí mať aspoň 8 znakov")
    .regex(/[A-Z]/, "Heslo musí obsahovať aspoň jedno veľké písmeno")
    .regex(/[a-z]/, "Heslo musí obsahovať aspoň jedno malé písmeno")
    .regex(/[0-9]/, "Heslo musí obsahovať aspoň jednu číslicu")
    .regex(/[^A-Za-z0-9]/, "Heslo musí obsahovať aspoň jeden špeciálny znak"),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Heslá sa nezhodujú",
  path: ["confirmPassword"]
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const [location] = useLocation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: ""
    }
  });

  useEffect(() => {
    // Extrahujeme token z URL
    const searchParams = new URLSearchParams(location.split("?")[1]);
    const tokenFromUrl = searchParams.get("token");
    
    if (!tokenFromUrl) {
      setError("Neplatný odkaz na obnovenie hesla");
      return;
    }
    
    setToken(tokenFromUrl);
  }, [location]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setError("Chýba token pre obnovenie hesla");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          password: data.password,
          confirmPassword: data.confirmPassword
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        toast({
          title: 'Heslo bolo úspešne zmenené',
          description: 'Teraz sa môžete prihlásiť s novým heslom',
          variant: 'default',
        });
      } else {
        setError(result.message || 'Nastala chyba pri obnovení hesla');
        toast({
          title: 'Chyba',
          description: result.message || 'Nastala chyba pri obnovení hesla',
          variant: 'destructive',
        });
      }
    } catch (err) {
      setError('Nastala chyba pri spracovaní vašej požiadavky');
      toast({
        title: 'Chyba',
        description: 'Nastala chyba pri spracovaní vašej požiadavky',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/40">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <h1 className="text-2xl font-bold tracking-tight">Obnovenie hesla</h1>
          <p className="text-sm text-muted-foreground">
            Nastavte si nové heslo pre váš účet
          </p>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="bg-destructive/10 p-4 rounded-lg flex items-start gap-3 mb-4">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-destructive">Chyba</h3>
                <p className="text-sm text-destructive/90">{error}</p>
              </div>
            </div>
          ) : null}

          {isSuccess ? (
            <div className="text-center py-8">
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-12 w-12 text-primary" />
              </div>
              <h2 className="text-xl font-medium mb-2">Heslo bolo úspešne zmenené</h2>
              <p className="text-muted-foreground mb-6">
                Teraz sa môžete prihlásiť do svojho účtu s novým heslom.
              </p>
              <Button asChild className="w-full">
                <Link href="/auth">Prihlásiť sa</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Nové heslo</Label>
                <Input
                  id="password"
                  type="password"
                  {...form.register("password")}
                  disabled={isSubmitting}
                />
                {form.formState.errors.password && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.password.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Potvrdenie nového hesla</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  {...form.register("confirmPassword")}
                  disabled={isSubmitting}
                />
                {form.formState.errors.confirmPassword && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting || !token}>
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Nastaviť nové heslo
              </Button>
            </form>
          )}
        </CardContent>
        <CardFooter className="flex justify-center border-t pt-6">
          <div className="text-center text-sm text-muted-foreground">
            <p>
              Pamätáte si svoje heslo?{" "}
              <Link href="/auth" className="text-primary hover:underline">
                Prihlásiť sa
              </Link>
            </p>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}